import React, { FunctionComponent, ReactNode, useState } from 'react';
import { Button, Divider, FormInstance, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { StyledLinkedTable, StyledTable } from '../common/tableView.styles';
import { SaveButtonIcon, StyledDeleteButton, StyledEditButton } from '../features/dictionaries/DictionaryItemsList';
import Icon, { ButtonIcon } from '../icons/Icon';

import { StyledButton } from './Button';
import { StyledRow } from './StyledRow';

export const AddIcon = styled(ButtonIcon).attrs({
  name: 'PlusSmall',
})``;

type TableWithDataUpdateProps<TData extends object, TForm extends object> = {
  title?: string;
  addButtonName?: string;
  duplicateButtonName?: string;
  columns: ColumnsType<TData>;
  dataSource: TData[];
  itemForm: FunctionComponent<{ form: FormInstance }>;
  duplicateForm?: FunctionComponent<{ form: FormInstance }>;
  withEdit?: boolean;
  withClone?: boolean;
  linkedCells?: boolean;
  predefinedFilters?: ReactNode;
  form: FormInstance;
  onSave: (record: TForm, successCallback?: () => void) => void;
  onClone?: (record: TForm, recruitments: React.Key[]) => void;
  onDelete?: (record: TData) => void;
};

export const EditButtons: React.FC<{ handleCancel: () => void; handleSave: () => void }> = ({
  handleCancel,
  handleSave,
}) => {
  return (
    <StyledRow flexGrow="0">
      <StyledButton onClick={handleCancel}>Cancel</StyledButton>
      <StyledButton type="primary" onClick={handleSave} icon={<SaveButtonIcon />} style={{ boxShadow: 'none' }}>
        Save
      </StyledButton>
    </StyledRow>
  );
};

export const TableWithDataUpdate = <TData extends object, TForm extends object>({
  title,
  addButtonName,
  columns,
  dataSource,
  itemForm: ItemForm,
  withEdit = false,
  linkedCells = false,
  form,
  onSave,
  onDelete,
  predefinedFilters: PredefinedFilters,
}: TableWithDataUpdateProps<TData, TForm>) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = (record: TData) => {
    form.setFieldsValue({ ...record });
    setIsEditing(true);
  };

  const handleDelete = (record: TData) => {
    onDelete?.(record);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    onSave(form.getFieldsValue(true) as TForm, () => setIsEditing(false));
  };

  const handleAddRow = () => {
    form.resetFields();
    setIsEditing(true);
  };

  const columnsWithEdit: ColumnsType<TData> = [
    ...columns,
    ...(withEdit
      ? [
          {
            title: '',
            dataIndex: 'operation',
            width: '15%',
            render: (_value: unknown, record: TData) => {
              return (
                <StyledRow justifyContent="space-evenly" style={{ alignItems: 'center' }}>
                  <StyledEditButton
                    type="text"
                    size="small"
                    onClick={() => handleEdit(record)}
                    icon={<Icon name="Edit" inlineStyle={false} />}
                  />
                  <StyledDeleteButton
                    type="text"
                    size="small"
                    onClick={() => handleDelete(record)}
                    icon={<Icon name="Delete" inlineStyle={false} />}
                  />
                </StyledRow>
              );
            },
          },
        ]
      : []),
  ];

  const pagination = {
    position: ['none', 'bottomCenter'],
    hideOnSinglePage: true,
  };

  const tableProps = {
    columns: columnsWithEdit,
    dataSource: dataSource,
    pagination: pagination,
    scroll: { y: 700 },
    bordered: true,
  };

  return (
    <div style={{ paddingBottom: '15px' }}>
      <StyledRow justifyContent="space-between">
        <Typography.Title level={5} style={{ paddingLeft: '15px' }}>
          {title}
        </Typography.Title>

        {isEditing ? (
          <EditButtons handleCancel={handleCancel} handleSave={handleSave} />
        ) : (
          <StyledRow flexGrow="0">
            <Button icon={<AddIcon />} onClick={handleAddRow}>
              {addButtonName}
            </Button>
            {PredefinedFilters}
          </StyledRow>
        )}
      </StyledRow>

      {isEditing && (
        <>
          <ItemForm form={form} />
          <Divider style={{ marginTop: '5px', marginBottom: '10px' }} />
        </>
      )}

      {linkedCells ? <StyledLinkedTable {...tableProps} /> : <StyledTable {...tableProps} />}
    </div>
  );
};
